import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useLogout } from "../../libs/hook/useLogout";
import styles from "./ModalMenu.module.scss";
import { modalMenuActions } from "@/entities/modalMenu";
import { useMe, useCurrentOrganization } from "@/Hooks";
import Spinner from "@/shared/ui/Spinner/Spinner";
import { Organization } from "@/features/auth/types/auth";
import { getGradientForLetter } from "@/Hooks/useGradient";
import SuggestAppModal from "@/widgets/suggestAppModal/SuggestAppModal";
import { useOpenProModal } from "@/Hooks/useOpenBillingModal";
import { sortByCategoriesActions } from "@/entities/sortByCategories";
import { filterAppsActions } from "@/entities/filterApps";
import { filterActions } from "@/entities/filter";
import { counterActions } from "@/entities/counter";
import { modalFiltersActions } from "@/entities/modalFilters/model/slice/modalFiltersSlice";
import { inputSearchActions } from "@/entities/inputSearch";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { useResetFilters } from "@/Hooks/useResetFilters";

interface ModalMenuProps {
  onClose: () => void;
  handleToggleList: () => void;
  handleSelectOrganization: (org: Organization) => void;
  displayImage: string | JSX.Element;
  isOrganizationListOpen: boolean;
  isLoadingOrganizations: boolean;
  organizationsList: Organization[];
}

const ModalMenu = React.forwardRef<HTMLDivElement, ModalMenuProps>(
  (
    {
      onClose,
      displayImage,
      handleToggleList,
      isOrganizationListOpen,
      isLoadingOrganizations,
      organizationsList,
      handleSelectOrganization,
    },
    ref,
  ) => {
    const { me } = useMe();
    const currentOrg = useCurrentOrganization();
    const { resetAllFilters } = useResetFilters(onClose);
    const dispatch = useDispatch();
    const { handleLogout, isLoggedOut } = useLogout();
    const accessToken = localStorage.getItem("access_token");
    const [isSuggestAppModalOpen, setIsSuggestAppModalOpen] =
      useState<boolean>(false);

    const organizationsWithGradients = organizationsList?.map((org) => ({
      ...org,
      gradient: getGradientForLetter(org.name.charAt(0)),
    }));

    const openProModal = useOpenProModal();

    const handleOpenBillingPage = () => {
      openProModal("Modal Menu");
    };

    return (
      <>
        <div
          className={styles.overlay}
          onClick={() => dispatch(modalMenuActions.hideModal())}
        />
        <div
          className={`${styles.ModalMenu} ModalMenu`}
          data-has-token={!!accessToken}
        >
          <div className={styles.header}>
            {me && (
              <>
                <div className={styles.header_organization_info_container}>
                  <div
                    className={styles.header_organization_info}
                    onClick={handleToggleList}
                  >
                    {displayImage}
                  </div>
                  {!me.is_pro && !currentOrg?.is_pro && (
                    <button
                      id="unlockProButton"
                      onClick={handleOpenBillingPage}
                      className={styles.billingButton}
                    >
                      Unlock Pro
                    </button>
                  )}
                </div>
                <div className={`${styles.line2} ${styles.line_mobile}`} />
              </>
            )}

            {!me && (
              <>
                <div className={styles.header_organization_info_container}>
                  <button
                    id="unlockProButton"
                    onClick={handleOpenBillingPage}
                    className={styles.billingButton}
                  >
                    Unlock Pro
                  </button>
                </div>
                <div
                  className={`${styles.line2} ${styles.line_mobile}`}
                  style={{ marginTop: "15px" }}
                />
              </>
            )}

            <div className={`${styles.list} ${styles.list_mobile}`}>
              <ul>
                <li>
                  <Link
                    to="/"
                    className={styles.link}
                    onClick={resetAllFilters}
                  >
                    <div className={styles.link_icon}>
                      <img
                        src="/img/blackIcon/smart-phone-01.svg"
                        alt="Explore Apps"
                      />
                    </div>
                    <span>Explore Apps</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/store-screenshots/"
                    className={styles.link}
                    onClick={resetAllFilters}
                  >
                    <div className={styles.link_icon}>
                      <img
                        src="/img/blackIcon/smart-phone-01-1.svg"
                        alt="App Store Screens"
                      />
                    </div>
                    <span>App Store Screens</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/animation-explorer"
                    className={styles.link}
                    onClick={resetAllFilters}
                  >
                    <div className={styles.link_icon}>
                      <img
                        src="/img/blackIcon/motion-01.svg"
                        alt="Animation Explorer"
                      />
                    </div>
                    <span>Animation Explorer</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/web-onboardings"
                    className={styles.link}
                    onClick={resetAllFilters}
                  >
                    <div className={styles.link_icon}>
                      <img
                        src="/img/blackIcon/browser.svg"
                        alt="Web Onboardings"
                      />
                    </div>
                    <span>Web Onboardings</span>
                  </Link>
                </li>
              </ul>
            </div>
            <div className={`${styles.line2} ${styles.line_mobile}`} />

            <div className={styles.list}>
              <ul>
                {me && (
                  <>
                    <li>
                      <Link
                        to="/settings/account"
                        className={styles.link}
                        onClick={onClose}
                      >
                        <img
                          src="/img/blackIcon/AccountBilling.svg"
                          alt="Account area and billing"
                        />
                        <span>Account area and billing</span>
                      </Link>
                    </li>
                    {/* <li>
                      <Link
                        to="/saved-library/starred/"
                        className={styles.link}
                        onClick={onClose}
                      >
                        <img
                          src="/img/blackIcon/Star.svg"
                          alt="Starred Apps Icon"
                        />
                        <span>Hidden & Favorite Apps</span>
                      </Link>
                    </li> */}
                    <li>
                      <Link
                        to="/saved-library"
                        className={styles.link}
                        onClick={onClose}
                      >
                        <img
                          src="/img/blackIcon/Save.svg"
                          alt="Saved Library"
                        />
                        <span>Saved Library</span>
                      </Link>
                    </li>
                  </>
                )}

                {!me && (
                  <li>
                    <Link to="/login" className={styles.link} onClick={onClose}>
                      <img src="/img/blackIcon/Logout.svg" alt="Login Icon" />
                      <span>Login</span>
                    </Link>
                  </li>
                )}

                <li>
                  <Link
                    to="/free-tools"
                    className={styles.link}
                    onClick={onClose}
                  >
                    <img
                      src="/img/blackIcon/MagicWand.svg"
                      alt="Starred Apps Icon"
                    />
                    <span>Free tools</span>
                  </Link>
                </li>

                <li>
                  <Link
                    to="/about-us"
                    className={styles.link}
                    onClick={onClose}
                  >
                    <img src="/img/blackIcon/AboutUs.svg" alt="About Us Icon" />
                    <span>About Us</span>
                  </Link>
                </li>

                <li>
                  <Link
                    to="https://screensdesign.com/knowledge-base-contact-us"
                    target="_blank"
                    className={styles.link}
                    onClick={onClose}
                  >
                    <img src="/img/blackIcon/Support.svg" alt="Support Icon" />
                    <span>Support</span>
                  </Link>
                </li>
                {!isLoggedOut && (
                  <li onClick={handleLogout}>
                    <img src="/img/blackIcon/Logout.svg" alt="Logout Icon" />
                    <span>Logout</span>
                  </li>
                )}
                <li>
                  <div className={styles.listLine} />
                </li>
                <li onClick={() => setIsSuggestAppModalOpen(true)}>
                  <img src="/img/message-add-menu.svg" alt="Suggest App" />
                  <span>Suggest App</span>
                </li>
                <li>
                  <Link
                    to="https://screensdesign.com/showcase"
                    target="_blank"
                    className={styles.link}
                    onClick={onClose}
                  >
                    <img src="/img/showcase-icon.svg" alt="Showcase Icon" />
                    <span>Showcase</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className={styles.line} />

            <div className={styles.horisList}>
              <ul>
                <li>
                  <Link
                    to="/privacy"
                    className={styles.horislink}
                    onClick={onClose}
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className={styles.horislink}
                    onClick={onClose}
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/copyright/"
                    className={styles.horislink}
                    onClick={onClose}
                  >
                    Copyright
                  </Link>
                </li>
              </ul>
            </div>

            {isOrganizationListOpen && (
              <div className={styles.OrganizationList}>
                {isLoadingOrganizations ? (
                  <div className={styles.loading}>
                    <Spinner />
                  </div>
                ) : organizationsList ? (
                  organizationsWithGradients
                    .sort((a, b) => {
                      const aUserOrg = me?.organizations?.find(
                        (userOrg) => userOrg.uuid === a.uuid,
                      );
                      const bUserOrg = me?.organizations?.find(
                        (userOrg) => userOrg.uuid === b.uuid,
                      );
                      if (aUserOrg?.is_pro && !bUserOrg?.is_pro) return -1;
                      if (!aUserOrg?.is_pro && bUserOrg?.is_pro) return 1;
                      return 0;
                    })
                    .map((org) => {
                      const userOrg = me?.organizations?.find(
                        (userOrg) => userOrg.uuid === org.uuid,
                      );
                      return (
                        <div
                          key={org.uuid}
                          className={styles.OrganizationList_Item}
                          onClick={() => handleSelectOrganization(org)}
                        >
                          <div className={styles.OrganizationList_Info}>
                            {org.image ? (
                              <img
                                src={org.image}
                                alt={org.name}
                                className={styles.OrganizationList_Image}
                              />
                            ) : (
                              <div
                                className={styles.OrganizationList_Image}
                                style={{
                                  background: userOrg.active
                                    ? "black"
                                    : org.gradient,
                                }}
                              >
                                {org.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span
                              className={styles.OrganizationList_Name}
                              title={org.name}
                            >
                              {org.name}
                            </span>
                          </div>
                          {userOrg && userOrg.active && (
                            <div className={styles.OrganizationList_Checkmark}>
                              <img
                                src="/img/whiteIcon/Check.svg"
                                alt="Active organization"
                              />
                            </div>
                          )}
                          {userOrg && userOrg.is_pro && (
                            <div
                              className={
                                styles.header_organization_container_pro_container
                              }
                            >
                              <p>PRO</p>
                            </div>
                          )}
                        </div>
                      );
                    })
                ) : (
                  <div className={styles.error}>
                    Failed to load organizations
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {isSuggestAppModalOpen && (
          <SuggestAppModal
            onClose={() => setIsSuggestAppModalOpen(false)}
            isOpen={isSuggestAppModalOpen}
          />
        )}
      </>
    );
  },
);

export default ModalMenu;
